import { parseISO, isWeekend, isToday } from 'date-fns';
import { ActivityCalendar } from '@linktivity/ars-neo';
import { HOLIDAYS, JAPANESE_LOCALE } from '@link/share/constants';

/**
 * Check if a time slot is in the valid display range based on date type
 * - Weekdays: 10:00-20:59
 * - Weekends/Holidays: 9:00-20:59
 * @param hour - The hour (0-23)
 * @param date - The date string in YYYY-MM-DD format
 * @returns true if the time is in valid range
 */
export const isTimeInValidRange = (hour: number, date: string): boolean => {
  // Check if the date is a holiday
  const isHoliday = Object.keys(HOLIDAYS).includes(date);

  // Check if the date is a weekend
  const dateObj = parseISO(date);
  const isWeekendDay = isWeekend(dateObj);

  const isWeekendOrHoliday = isHoliday || isWeekendDay;

  // Hide times after 21:00 for all days
  if (hour >= 21) {
    return false;
  }

  // For weekends or holidays, show times from 9:00 onwards
  if (isWeekendOrHoliday) {
    return hour >= 9;
  }

  // For weekdays, show times from 10:00 onwards
  return hour >= 10;
};

/**
 * Extract hour from time string
 * @param time - Time string in format HH:mm or HH:mm:ss
 * @returns hour number or null if invalid
 */
export const extractHourFromTime = (time: string): number | null => {
  const timeMatch = time.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!timeMatch) {
    return null;
  }
  return parseInt(timeMatch[1], 10);
};

/**
 * Filter start times based on date type (weekday/weekend/holiday) and time range
 * - Weekdays: 10:00-21:00
 * - Weekends/Holidays: 9:00-21:00
 * @param startTime - The start time object with title or time property
 * @param date - The date string in YYYY-MM-DD format
 * @returns true if the time should be displayed, false otherwise
 */
export const shouldDisplayStartTime = (
  startTime: { title?: string; time?: string },
  date: string
): boolean => {
  // The canonical `time` field wins; `title` is display text and is only a
  // fallback for data that carries the time nowhere else.
  const timeString = startTime.time || startTime.title;

  if (!timeString || !date) {
    return true; // Show by default if data is missing
  }

  const hour = extractHourFromTime(timeString);
  if (hour === null) {
    return true; // Show by default if time format is invalid
  }

  return isTimeInValidRange(hour, date);
};

// Mapping from original Japanese unit names to their localized display forms.
const JA_UNIT_NAME_MAP: Record<string, string> = {
  子供: 'こども',
  介護者: '障がい者（付添）'
};

/**
 * Localize unit names for display.
 * Currently applies Japanese-specific replacements when the language is JAPANESE_LOCALE.
 *
 * @param name - Original unit name to localize.
 * @param language - BCP-47 language tag or locale identifier used to decide whether to localize.
 * @returns The localized unit name, or the original name if no localization is applied.
 */
export const localizeUnitName = (
  name: string | undefined,
  language: string
): string | undefined => {
  if (!name || language !== JAPANESE_LOCALE) return name;
  let result = name;
  for (const [key, value] of Object.entries(JA_UNIT_NAME_MAP)) {
    result = result.replace(new RegExp(key, 'g'), value);
  }
  return result;
};

/**
 * Localize unit names across an entire activity calendar.
 * Deep-maps calendar → dailyAvailabilities → planInfos → unitItems,
 * applying {@link localizeUnitName} to each unit.
 *
 * Short-circuits when the language is not Japanese to avoid unnecessary allocations.
 */
export const localizeCalendarUnitNames = (
  calendar: ActivityCalendar | undefined,
  language: string
): ActivityCalendar | undefined => {
  if (!calendar?.dailyAvailabilities) return calendar;
  if (language !== JAPANESE_LOCALE) return calendar;
  return {
    ...calendar,
    dailyAvailabilities: calendar.dailyAvailabilities.map(day => ({
      ...day,
      planInfos: day.planInfos?.map(plan => ({
        ...plan,
        unitItems: plan.unitItems?.map(unit => ({
          ...unit,
          name: localizeUnitName(unit.name, language) ?? unit.name
        }))
      }))
    }))
  };
};

/**
 * Check if tip should be shown for a time slot
 * Shows tip for sold-out time slots that are in valid operating hours,
 * but not for today or the special excluded window on 2026-07-25 (16:00-20:30)
 * @param time - Time string in format HH:mm
 * @param date - The date string in YYYY-MM-DD format
 * @returns true if tip should be shown
 */
export const shouldShowTimeSlotTip = (time: string, date: string): boolean => {
  if (!time || !date) {
    return false;
  }

  // Don't show tip for today
  const dateObj = parseISO(date);
  if (isToday(dateObj)) {
    return false;
  }

  const timeMatch = time.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!timeMatch) {
    return false;
  }

  const hour = parseInt(timeMatch[1], 10);
  const minute = parseInt(timeMatch[2], 10);

  // Special rule (LINK-4692): hide tips only for 16:00-20:30 on 2026-07-25
  if (date === '2026-07-25') {
    const totalMinutes = hour * 60 + minute;
    if (totalMinutes >= 16 * 60 && totalMinutes <= 20 * 60 + 30) {
      return false;
    }
  }

  return isTimeInValidRange(hour, date);
};
